import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useEffect, useMemo } from "react"
import { useAuthStore } from "@/stores/auth"
import { useGetUserQuery } from "@/queries/user"
import { useSetActiveBusiness } from "@/hooks/use-business"
import { AppBootstrapLoader } from "@/components/loaders/preloader"
import { LOGIN } from "@/constants/paths/shared"
import { ADMIN_HOME } from "@/constants/paths/admin"
import { isSuperAdmin } from "@/constants/user-types"
import {
  effectiveConsoleScope,
  getEligibleConsoleScopes,
  getDefaultActiveBusinessId,
  hasMultipleTenantBusinesses,
  mergeTenantBusinessSummaries,
  needsConsoleScopeChooser,
} from "@/helpers/console-scope"
import {
  CONSOLE_SCOPE_PLATFORM,
  SELECT_CONSOLE_SCOPE_PATH,
} from "@/constants/console-scope"
import type { AuthSession } from "@/types/auth"

export function AuthGuard() {
  const location = useLocation()
  const token = useAuthStore((state) => state.token)
  const storeUser = useAuthStore((state) => state.user)
  const consoleScope = useAuthStore((state) => state.consoleScope)
  const setUser = useAuthStore((state) => state.setUser)
  const activeBusinessId = useAuthStore((state) => state.activeBusinessId)
  const setActiveBusiness = useSetActiveBusiness()
  const shouldSkipProfileFetch = false
  const profileQueryEnabled = !!token && !shouldSkipProfileFetch
  const { data, isLoading: isProfileQueryLoading } = useGetUserQuery(
    {},
    { skip: !profileQueryEnabled }
  )
  // LocaleSync/header share this query key; only block routes when AuthGuard needs the fetch.
  const isLoading = profileQueryEnabled && isProfileQueryLoading

  const userData =
    (data as AuthSession | undefined) ??
    (storeUser as AuthSession | null | undefined) ??
    undefined

  const tenantBusinessList = useMemo(
    () => mergeTenantBusinessSummaries(userData ?? null),
    [userData]
  )

  const eligible = useMemo(
    () => getEligibleConsoleScopes(userData ?? null),
    [userData]
  )

  const effectiveScope = effectiveConsoleScope(consoleScope, userData ?? null)

  useEffect(() => {
    if (data) {
      setUser(data as { [key: string]: unknown })
    }
  }, [data, setUser])

  useEffect(() => {
    if (!isLoading && tenantBusinessList.length > 0) {
      const persistedActiveId = useAuthStore.getState().activeBusinessId
      if (persistedActiveId) {
        const businessExists = tenantBusinessList.some(
          (b) => b._id === persistedActiveId
        )
        if (!businessExists) {
          setActiveBusiness(null)
        }
      } else {
        const defaultBusinessId = getDefaultActiveBusinessId(userData ?? null)
        if (defaultBusinessId) {
          setActiveBusiness(defaultBusinessId)
        }
      }
    }
  }, [tenantBusinessList, isLoading, setActiveBusiness, userData])

  if (!token) {
    const redirectPath = location.pathname === "/user-profile" ? "" : location.pathname + location.search
    return <Navigate to={LOGIN} state={{ redirect: redirectPath }} replace />
  }

  if (isLoading) {
    return <AppBootstrapLoader />
  }

  const currentPath = location.pathname

  if (currentPath.startsWith("/admin")) {
    return <Outlet />
  }

  if (
    needsConsoleScopeChooser(eligible, effectiveScope) &&
    currentPath !== SELECT_CONSOLE_SCOPE_PATH
  ) {
    return <Navigate to={SELECT_CONSOLE_SCOPE_PATH} replace />
  }

  if (
    effectiveScope === CONSOLE_SCOPE_PLATFORM &&
    !currentPath.startsWith("/admin") &&
    currentPath !== SELECT_CONSOLE_SCOPE_PATH &&
    currentPath !== "/select-business" &&
    currentPath !== "/business/create" &&
    currentPath !== "/business/import"
  ) {
    return <Navigate to={ADMIN_HOME} replace />
  }

  if (
    isSuperAdmin(userData) &&
    tenantBusinessList.length === 0 &&
    (currentPath === "/dashboard" || currentPath === "/")
  ) {
    return <Navigate to={ADMIN_HOME} replace />
  }

  if (
    currentPath === "/select-business" ||
    currentPath === "/business/create" ||
    currentPath === "/business/import" ||
    currentPath === SELECT_CONSOLE_SCOPE_PATH
  ) {
    return <Outlet />
  }

  if (hasMultipleTenantBusinesses(userData ?? null) && !activeBusinessId) {
    return <Navigate to="/select-business" replace />
  }

  return <Outlet />
}

export default AuthGuard
